/**
 * Subtle social proof widget
 * Shows anonymized user success stories to build trust and FOMO
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Card } from './ui/card';
import { TrendingDown, Users, Sparkles, ChevronLeft, ChevronRight, Heart } from 'lucide-react';
import { MotionDiv } from '../lib/motion-utils';

interface SuccessStory {
  metric: string;
  improvement: string;
  timeframe: string;
  plan: 'premium' | 'pro';
  context: string;
}

const SUCCESS_STORIES: SuccessStory[] = [
  {
    metric: "RTI Score dropped from 82 → 34",
    improvement: "58 point decrease",
    timeframe: "6 days",
    plan: "premium",
    context: "Used forecast + restraint strategy"
  },
  {
    metric: "Initiation balanced to 50/50",
    improvement: "Was 87% self-initiation",
    timeframe: "4 days",
    plan: "premium",
    context: "Applied availability calibration"
  },
  {
    metric: "Response latency normalized",
    improvement: "6hr → 45min avg",
    timeframe: "3 days",
    plan: "premium",
    context: "Strategic space then re-engagement"
  },
  {
    metric: "Broke pursuit-withdrawal cycle",
    improvement: "Pattern lasted 3 weeks",
    timeframe: "5 days",
    plan: "pro",
    context: "ML-detected pattern + intervention"
  },
  {
    metric: "Emotional density recovered",
    improvement: "From flat to engaged",
    timeframe: "7 days",
    plan: "premium",
    context: "Used reconnection action cards"
  },
  {
    metric: "Maintained <30 RTI for 2 months",
    improvement: "Was averaging 65+",
    timeframe: "ongoing",
    plan: "pro",
    context: "Monthly coaching + deep analysis"
  },
  {
    metric: "Avoided breakup crisis",
    improvement: "RTI peaked at 91",
    timeframe: "48 hours",
    plan: "premium",
    context: "72hr forecast gave early warning"
  },
  {
    metric: "Rebuilt after 2-week silence",
    improvement: "Complete withdrawal",
    timeframe: "10 days",
    plan: "premium",
    context: "Reconnection framework + timing"
  }
];

export function SocialProofWidget() {
  const navigate = useNavigate();
  const [currentStory, setCurrentStory] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [autoRotate, setAutoRotate] = useState(true);
  const [reactionByStory, setReactionByStory] = useState<Record<number, 'relatable' | 'want-this' | null>>({});

  // Rotate through stories every 8 seconds
  useEffect(() => {
    if (!autoRotate) return;
    const interval = setInterval(() => {
      setCurrentStory((prev) => (prev + 1) % SUCCESS_STORIES.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [autoRotate]);

  // Check if user dismissed
  useEffect(() => {
    const dismissed = localStorage.getItem('tension_social_proof_dismissed');
    if (dismissed) {
      setIsVisible(false);
    }
  }, []);

  const handleClick = () => {
    navigate('/pricing');
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    localStorage.setItem('tension_social_proof_dismissed', 'true');
    setIsVisible(false);
  };

  if (!isVisible) return null;

  const story = SUCCESS_STORIES[currentStory];
  const selectedReaction = reactionByStory[currentStory];
  const goPrev = () => setCurrentStory((prev) => (prev - 1 + SUCCESS_STORIES.length) % SUCCESS_STORIES.length);
  const goNext = () => setCurrentStory((prev) => (prev + 1) % SUCCESS_STORIES.length);

  return (
    <MotionDiv
      key={currentStory}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className="mb-4"
    >
      <Card 
        className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 border-green-200 dark:border-green-800 cursor-pointer hover:shadow-md transition-shadow"
        onClick={handleClick}
        onMouseEnter={() => setAutoRotate(false)}
        onMouseLeave={() => setAutoRotate(true)}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            <div className="w-8 h-8 rounded-full bg-green-600/20 flex items-center justify-center flex-shrink-0">
              <TrendingDown className="w-4 h-4 text-green-700 dark:text-green-400" />
            </div>
            
            <div className="space-y-1 flex-1">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                  {story.metric}
                </p>
                <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded-full">
                  {story.plan.toUpperCase()}
                </span>
              </div>
              
              <p className="text-xs text-neutral-600 dark:text-neutral-400">
                {story.improvement} • {story.timeframe}
              </p>
              
              <p className="text-xs text-neutral-500 dark:text-neutral-500 italic">
                "{story.context}"
              </p>
            </div>
          </div>

          <button
            onClick={handleDismiss}
            className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors ml-2"
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>

        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
            className="h-7 w-7 rounded-full border border-green-300 dark:border-green-700 flex items-center justify-center text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/40"
            aria-label="Previous success story"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
            className="h-7 w-7 rounded-full border border-green-300 dark:border-green-700 flex items-center justify-center text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/40"
            aria-label="Next success story"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <div className="ml-auto flex items-center gap-1.5">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setReactionByStory((prev) => ({ ...prev, [currentStory]: 'relatable' }));
              }}
              className={`rounded-full px-2 py-1 text-[11px] border transition-colors ${
                selectedReaction === 'relatable'
                  ? 'border-green-500 bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
                  : 'border-green-300 text-green-700 dark:border-green-700 dark:text-green-300'
              }`}
            >
              relatable
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setReactionByStory((prev) => ({ ...prev, [currentStory]: 'want-this' }));
              }}
              className={`rounded-full px-2 py-1 text-[11px] border flex items-center gap-1 transition-colors ${
                selectedReaction === 'want-this'
                  ? 'border-green-500 bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
                  : 'border-green-300 text-green-700 dark:border-green-700 dark:text-green-300'
              }`}
            >
              <Heart className="w-3 h-3" />
              want this
            </button>
          </div>
        </div>

        {/* Progress dots */}
        <div className="flex items-center gap-1 mt-3 justify-center">
          {SUCCESS_STORIES.map((_, idx) => (
            <div
              key={idx}
              className={`h-1 rounded-full transition-all ${
                idx === currentStory 
                  ? 'w-6 bg-green-600' 
                  : 'w-1 bg-green-300 dark:bg-green-700'
              }`}
            />
          ))}
        </div>
      </Card>
    </MotionDiv>
  );
}
